// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Modal, Dropdown, TextInput, TextArea, Button, StatusBanner, FormRow } from '@loa/ui-components';
import type { SelectOption } from '@loa/ui-components';
import { quoteTermApi } from '../api/quoteTermApi';
import type { QuoteTerm, QuoteTermOption, QuoteTermAllocationBand } from '../api/quoteTermApi';

type Props = Readonly<{
  open: boolean;
  providerId: string;
  productOptions: SelectOption[];
  existing?: QuoteTerm | null;
  onClose: () => void;
  onSaved: () => void;
}>;

const BASIS_OPTIONS = [
  { value: 'GROSS', text: 'Gross (banded)' },
  { value: 'NET', text: 'Net (banded)' },
  { value: 'FLAT', text: 'Flat' },
];

const FEE_FREQUENCY_OPTIONS = [
  { value: 'ANNUAL', text: 'Per annum' },
  { value: 'MONTHLY', text: 'Monthly' },
  { value: 'QUARTERLY', text: 'Quarterly' },
  { value: 'ONE_OFF', text: 'One-off' },
];

/**
 * Editor working shape: the charge-schedule fields are held as raw strings so the
 * advisor can type commas / % / slashes freely (the value is only parsed to a
 * number[] on save). Everything else mirrors {@link QuoteTermOption}.
 */
type MetadataEntry = { key: string; value: string };

type OptionDraft = Omit<QuoteTermOption, 'exitCharges' | 'commissionClawback' | 'metadata'> & {
  exitChargesText: string;
  commissionClawbackText: string;
  metadataEntries: MetadataEntry[];
};

const emptyOption = (): OptionDraft => ({
  optionCode: '',
  allocationBasis: 'GROSS',
  bands: [{ allocationRate: 100 }],
  exitChargesText: '',
  commissionClawbackText: '',
  metadataEntries: [],
});

const toDraft = (o: QuoteTermOption): OptionDraft => ({
  ...o,
  exitChargesText: scheduleToText(o.exitCharges),
  commissionClawbackText: scheduleToText(o.commissionClawback),
  metadataEntries: Object.entries(o.metadata ?? {}).map(([key, value]) => ({ key, value: String(value) })),
});

/** Collapse the key-value rows into a Record, dropping blank keys. Null when empty. */
const entriesToRecord = (entries: MetadataEntry[]): Record<string, unknown> | null => {
  const record: Record<string, unknown> = {};
  for (const { key, value } of entries) {
    const k = key.trim();
    if (k) record[k] = value;
  }
  return Object.keys(record).length ? record : null;
};

const numOrNull = (v: string): number | null => (v === '' || v == null ? null : Number(v));
const scheduleToText = (v?: number[]): string => (v && v.length ? v.join(', ') : '');
/** Tolerant parse: split on comma / slash / whitespace, strip %, keep valid numbers. */
const parseSchedule = (v: string): number[] =>
  v
    .split(/[,/\s]+/)
    .map((s) => s.replace('%', '').trim())
    .filter((s) => s !== '')
    .map(Number)
    .filter((n) => !Number.isNaN(n));

export function QuoteTermsEditorModal({ open, providerId, productOptions, existing, onClose, onSaved }: Props) {
  const [productId, setProductId] = useState('');
  const [currencyCode, setCurrencyCode] = useState('EUR');
  const [notes, setNotes] = useState('');
  const [options, setOptions] = useState<OptionDraft[]>([emptyOption()]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setProductId(existing.productId);
      setCurrencyCode(existing.currencyCode ?? 'EUR');
      setNotes(existing.notes ?? '');
      setOptions(existing.options?.length ? existing.options.map(toDraft) : [emptyOption()]);
    } else {
      setProductId('');
      setCurrencyCode('EUR');
      setNotes('');
      setOptions([emptyOption()]);
    }
    setError(null);
  }, [existing, open]);

  const setOption = (idx: number, patch: Partial<OptionDraft>) =>
    setOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  const setBand = (oi: number, bi: number, patch: Partial<QuoteTermAllocationBand>) =>
    setOptions((prev) => prev.map((o, i) => {
      if (i !== oi) return o;
      return { ...o, bands: (o.bands ?? []).map((b, j) => (j === bi ? { ...b, ...patch } : b)) };
    }));
  const setMetaEntry = (oi: number, mi: number, patch: Partial<MetadataEntry>) =>
    setOption(oi, {
      metadataEntries: (options[oi].metadataEntries ?? []).map((e, j) => (j === mi ? { ...e, ...patch } : e)),
    });

  const handleSave = async () => {
    setError(null);
    if (!productId) { setError('Select a product'); return; }
    const mappedOptions: QuoteTermOption[] = options.map(
      ({ exitChargesText, commissionClawbackText, metadataEntries, ...rest }) => ({
        ...rest,
        exitCharges: parseSchedule(exitChargesText),
        commissionClawback: parseSchedule(commissionClawbackText),
        metadata: entriesToRecord(metadataEntries),
      }),
    );
    const body: QuoteTerm = {
      ...(existing ?? {}),
      providerId,
      productId,
      currencyCode,
      isActive: existing?.isActive ?? true,
      notes,
      options: mappedOptions,
    };
    setSaving(true);
    try {
      if (existing?.id) await quoteTermApi.update(existing.id, body);
      else await quoteTermApi.create(body);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="xlarge">
      <Modal.Header icon="document" title={existing ? 'Edit Terms & Quotes' : 'Add Terms & Quotes'} onClose={onClose} />
      <Modal.Content>
        {error && <StatusBanner variant="error" message={error} visible position="inline" dismissible={false} />}

        <div className="flex flex-col gap-xl">
          <FormRow>
            <Dropdown
              label="Product" placeholder="Choose a product" options={productOptions}
              value={productId} onChange={setProductId} search fluid required
            />
            <TextInput
              label="Currency" value={currencyCode} maxLength={3} fluid
              onChange={(e) => setCurrencyCode(e.target.value.toUpperCase())}
            />
          </FormRow>
          <TextArea label="Notes" value={notes} rows={2} fluid onChange={(e) => setNotes(e.target.value)} />

          {options.map((opt, oi) => (
            <div key={oi} className="border border-grey-06 rounded-lg p-4 flex flex-col gap-lg" data-testid={`opt-${oi}`}>
              <FormRow>
                <TextInput label="Option code" value={opt.optionCode} required fluid
                  onChange={(e) => setOption(oi, { optionCode: e.target.value })} />
                <TextInput label="Name" value={opt.optionName ?? ''} fluid
                  onChange={(e) => setOption(oi, { optionName: e.target.value })} />
                <Dropdown label="Allocation basis" options={BASIS_OPTIONS} value={opt.allocationBasis} fluid
                  onChange={(v) => setOption(oi, { allocationBasis: v as QuoteTermOption['allocationBasis'] })} />
              </FormRow>

              <span className="text-body-04 font-medium text-grey-02">Fund charges (%)</span>
              <FormRow>
                <TextInput label="Standard" type="number" step="0.01" value={opt.standardFundCharge ?? ''} fluid
                  onChange={(e) => setOption(oi, { standardFundCharge: numOrNull(e.target.value) })} />
                <TextInput label="Passive" type="number" step="0.01" value={opt.passiveFundCharge ?? ''} fluid
                  onChange={(e) => setOption(oi, { passiveFundCharge: numOrNull(e.target.value) })} />
                <TextInput label="Fixed / My Future allocation" type="number" step="0.01"
                  value={opt.fixedAllocationFundCharge ?? ''} fluid
                  onChange={(e) => setOption(oi, { fixedAllocationFundCharge: numOrNull(e.target.value) })} />
              </FormRow>

              {opt.allocationBasis === 'FLAT' ? (
                <TextInput label="Flat allocation rate (%)" type="number" step="0.01" value={opt.flatAllocationRate ?? ''} fluid
                  onChange={(e) => setOption(oi, { flatAllocationRate: numOrNull(e.target.value) })} />
              ) : (
                <div className="flex flex-col gap-sm">
                  <span className="text-body-04 font-medium text-grey-02">
                    Allocation rates (contribution min / max / rate %)
                  </span>
                  {(opt.bands ?? []).map((b, bi) => (
                    <FormRow key={bi}>
                      <TextInput type="number" placeholder="min" value={b.contributionMin ?? ''} fluid
                        onChange={(e) => setBand(oi, bi, { contributionMin: numOrNull(e.target.value) })} />
                      <TextInput type="number" placeholder="max" value={b.contributionMax ?? ''} fluid
                        onChange={(e) => setBand(oi, bi, { contributionMax: numOrNull(e.target.value) })} />
                      <TextInput type="number" placeholder="rate" value={b.allocationRate ?? ''} fluid
                        onChange={(e) => setBand(oi, bi, { allocationRate: Number(e.target.value) })} />
                      <Button variant="outlined" size="sm" type="button"
                        onClick={() => setOption(oi, { bands: (opt.bands ?? []).filter((_, j) => j !== bi) })}>
                        Remove
                      </Button>
                    </FormRow>
                  ))}
                  <div>
                    <Button variant="outlined" size="sm" type="button"
                      onClick={() => setOption(oi, { bands: [...(opt.bands ?? []), { allocationRate: 100 }] })}>
                      Add band
                    </Button>
                  </div>
                </div>
              )}

              <span className="text-body-04 font-medium text-grey-02">Commission (%)</span>
              <FormRow>
                <TextInput label="Gross initial — min" type="number" step="0.01" value={opt.initialCommissionMin ?? ''} fluid
                  onChange={(e) => setOption(oi, { initialCommissionMin: numOrNull(e.target.value) })} />
                <TextInput label="Gross initial — max" type="number" step="0.01" value={opt.initialCommissionMax ?? ''} fluid
                  onChange={(e) => setOption(oi, { initialCommissionMax: numOrNull(e.target.value) })} />
                <TextInput label="Gross initial — fixed" type="number" step="0.01" value={opt.initialCommissionFixed ?? ''} fluid
                  onChange={(e) => setOption(oi, { initialCommissionFixed: numOrNull(e.target.value) })} />
              </FormRow>
              <FormRow>
                <TextInput label="Fund-based — min" type="number" step="0.01" value={opt.fundBasedCommissionMin ?? ''} fluid
                  onChange={(e) => setOption(oi, { fundBasedCommissionMin: numOrNull(e.target.value) })} />
                <TextInput label="Fund-based — max" type="number" step="0.01" value={opt.fundBasedCommissionMax ?? ''} fluid
                  onChange={(e) => setOption(oi, { fundBasedCommissionMax: numOrNull(e.target.value) })} />
                <TextInput label="Accrual rate" placeholder="e.g. 0.38% × Term" value={opt.accrualRate ?? ''} fluid
                  onChange={(e) => setOption(oi, { accrualRate: e.target.value })} />
              </FormRow>

              <span className="text-body-04 font-medium text-grey-02">Contribution &amp; term</span>
              <FormRow>
                <TextInput label="Min contribution" type="number" value={opt.minContribution ?? ''} fluid
                  onChange={(e) => setOption(oi, { minContribution: numOrNull(e.target.value) })} />
                <TextInput label="Max contribution" type="number" value={opt.maxContribution ?? ''} fluid
                  onChange={(e) => setOption(oi, { maxContribution: numOrNull(e.target.value) })} />
                <TextInput label="Min term (years)" type="number" value={opt.minTermYears ?? ''} fluid
                  onChange={(e) => setOption(oi, { minTermYears: numOrNull(e.target.value) })} />
                <TextInput label="Max NRA for commission" type="number" value={opt.maxNraForCommission ?? ''} fluid
                  onChange={(e) => setOption(oi, { maxNraForCommission: numOrNull(e.target.value) })} />
              </FormRow>

              <span className="text-body-04 font-medium text-grey-02">Exit &amp; clawback schedules</span>
              <FormRow>
                <TextInput label="Exit charges (%)" placeholder="e.g. 5, 5, 5, 3, 2"
                  helperText="Comma- or /-separated, per year" value={opt.exitChargesText} fluid
                  onChange={(e) => setOption(oi, { exitChargesText: e.target.value })} />
                <TextInput label="Commission clawback (%)" placeholder="e.g. 100, 75, 50, 25"
                  helperText="Comma- or /-separated, per year" value={opt.commissionClawbackText} fluid
                  onChange={(e) => setOption(oi, { commissionClawbackText: e.target.value })} />
              </FormRow>

              <span className="text-body-04 font-medium text-grey-02">Fees &amp; terms</span>
              <FormRow>
                <TextInput label="Policy fee amount" type="number" value={opt.policyFeeAmount ?? ''} fluid
                  onChange={(e) => setOption(oi, { policyFeeAmount: numOrNull(e.target.value) })} />
                <Dropdown label="Policy fee frequency" options={FEE_FREQUENCY_OPTIONS} clearable fluid
                  value={opt.policyFeeFrequency ?? ''}
                  onChange={(v) => setOption(oi, { policyFeeFrequency: v || null })} />
                <TextInput label="Fund switches" placeholder="e.g. Free" value={opt.fundSwitches ?? ''} fluid
                  onChange={(e) => setOption(oi, { fundSwitches: e.target.value })} />
                <TextInput label="Customer withdrawals" placeholder="e.g. Up to 7.5% without penalty"
                  value={opt.customerWithdrawals ?? ''} fluid
                  onChange={(e) => setOption(oi, { customerWithdrawals: e.target.value })} />
              </FormRow>

              <span className="text-body-04 font-medium text-grey-02">Additional fields (provider-specific)</span>
              {(opt.metadataEntries ?? []).map((entry, mi) => (
                <FormRow key={mi}>
                  <TextInput label="Field name" placeholder="e.g. S-code" value={entry.key} fluid
                    onChange={(e) => setMetaEntry(oi, mi, { key: e.target.value })} />
                  <TextInput label="Value" placeholder="e.g. S520" value={entry.value} fluid
                    onChange={(e) => setMetaEntry(oi, mi, { value: e.target.value })} />
                  <Button variant="outlined" size="sm" type="button"
                    onClick={() => setOption(oi, {
                      metadataEntries: (opt.metadataEntries ?? []).filter((_, j) => j !== mi),
                    })}>
                    Remove
                  </Button>
                </FormRow>
              ))}
              <div>
                <Button variant="outlined" size="sm" type="button"
                  onClick={() => setOption(oi, { metadataEntries: [...(opt.metadataEntries ?? []), { key: '', value: '' }] })}>
                  Add field
                </Button>
              </div>

              {options.length > 1 && (
                <div>
                  <Button variant="outlined" size="sm" colour="red" type="button"
                    onClick={() => setOptions((prev) => prev.filter((_, i) => i !== oi))}>
                    Remove option
                  </Button>
                </div>
              )}
            </div>
          ))}

          <div>
            <Button variant="outlined" size="sm" type="button"
              onClick={() => setOptions((prev) => [...prev, emptyOption()])}>
              Add option
            </Button>
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: saving }}
        primaryAction={{ label: 'Save', onClick: () => { void handleSave(); }, loading: saving, disabled: saving }}
      />
    </Modal>
  );
}
